/**
 * Legal Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/documenti';
import type { Contratto, ContrattoInsert, ContrattoUpdate, ContrattoTemplate, ContrattoTemplateInsert, ContrattoTemplateUpdate } from './types';

const CONTRATTI_KEY = 'contratti';
const TEMPLATES_KEY = 'contract_templates';

// ============================================================
// CONTRATTI QUERIES
// ============================================================

export function useContratti() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CONTRATTI_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getContratti({ userId: user?.id });
      if (error) throw error;
      return (data || []) as Contratto[];
    },
    enabled: !!user?.id,
  });
}

export function useContratto(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CONTRATTI_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getContrattoById(id, user?.id);
      if (error) throw error;
      return data as Contratto | null;
    },
    enabled: !!id && !!user?.id,
  });
}

export function useContrattiInScadenza(giorni: number = 30) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CONTRATTI_KEY, 'scadenza', giorni, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getContrattiInScadenza(giorni);
      if (error) throw error;
      return (data || []) as Contratto[];
    },
    enabled: !!user?.id,
  });
}

// ============================================================
// CONTRATTI MUTATIONS
// ============================================================

export function useCreateContratto() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (contratto: ContrattoInsert) => {
      const { data, error } = await api.createContratto(contratto, user?.id);
      if (error) throw error;
      return data as Contratto;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTRATTI_KEY] });
    },
  });
}

export function useUpdateContratto() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ContrattoUpdate }) => {
      const { data: result, error } = await api.updateContratto(id, data, user?.id);
      if (error) throw error;
      return result as Contratto;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTRATTI_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONTRATTI_KEY, variables.id] });
    },
  });
}

export function useDeleteContratto() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deleteContratto(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTRATTI_KEY] });
    },
  });
}

// ============================================================
// TEMPLATES QUERIES
// ============================================================

export function useContrattoTemplates() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [TEMPLATES_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getContrattoTemplates({ userId: user?.id });
      if (error) throw error;
      return (data || []) as ContrattoTemplate[];
    },
    enabled: !!user?.id,
  });
}

export function useContrattoTemplate(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [TEMPLATES_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getContrattoTemplateById(id, user?.id);
      if (error) throw error;
      return data as ContrattoTemplate | null;
    },
    enabled: !!id && !!user?.id,
  });
}

// ============================================================
// TEMPLATES MUTATIONS
// ============================================================

export function useCreateContrattoTemplate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (template: ContrattoTemplateInsert) => {
      const { data, error } = await api.createContrattoTemplate(template, user?.id);
      if (error) throw error;
      return data as ContrattoTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

export function useUpdateContrattoTemplate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ContrattoTemplateUpdate }) => {
      const { data: result, error } = await api.updateContrattoTemplate(id, data, user?.id);
      if (error) throw error;
      return result as ContrattoTemplate;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, variables.id] });
    },
  });
}

export function useDeleteContrattoTemplate() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deleteContrattoTemplate(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

// ============================================================
// STATS
// ============================================================

export function useContrattiStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [CONTRATTI_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { stats, error } = await api.getContrattiStats(user?.id);
      if (error) throw error;
      return stats;
    },
    enabled: !!user?.id,
  });
}
